import toast, { Renderable, Toast, ValueFunction } from "react-hot-toast";
import { Address, Client } from "viem";
import { TicketMapper } from "../models/TicketMapper";
import { waitForCallsStatus, waitForTransactionReceipt } from "viem/actions";
import { CHAIN, isMiniApp } from "./Constants";
import { Ticket } from "../models/Ticket";
import { sdk } from "@farcaster/miniapp-sdk";
import { USDC } from "../models/UsdcValue";
import { SentryUtils } from "./SentryUtils";
import { combinationEquals, xSet, xUpkeepConfig } from "../web3/Types";
import { PrizeTierResults } from "../models/PrizeTierResults";

export function snackBar(
  message: Renderable | ValueFunction<Renderable, Toast>,
  emoji: string,
  ms?: number | undefined,
) {
  toast(message, {
    duration: ms ?? 5000,
    icon: emoji,
    style: {
      background: "#0e0d13",
      color: "#C0C0C0",
      fontFamily: "Poppins",
      fontSize: "14px",
    },
  });
}

export function dismissSnackbar() {
  toast.dismiss();
}

const nth = (number: number) => {
  if (number > 3 && number < 21) return `${number}th`;
  switch (number % 10) {
    case 1:
      return `${number}st`;
    case 2:
      return `${number}nd`;
    case 3:
      return `${number}rd`;
    default:
      return `${number}th`;
  }
};

export function handleError(
  errorMessage: string,
  failurePoint: string,
  errorLog?: string,
  snackBarContent?: Renderable | ValueFunction<Renderable, Toast>,
) {
  console.error(
    `An error occurred in "${failurePoint}" - ${errorLog ?? errorMessage}`,
  );
  SentryUtils.customIssue(
    `An error occurred in "${failurePoint}" - ${errorLog ?? errorMessage}`,
    "error",
  );
  dismissSnackbar();
  snackBar(snackBarContent ?? errorMessage, "❌");
}

export function handleEventError(context: string, e: unknown) {
  const error = `Failed to parse ${context} event. ${e}`;
  SentryUtils.customIssue(error, "error");
  console.error(error);
}

export function formatDate(
  date?: Date,
  withTimeOfDay: boolean = true,
): string | undefined {
  if (date == undefined) return undefined;
  const day: string = nth(date.getDate());
  const month: string = date.toLocaleString("default", { month: "long" });
  const year: number = date.getFullYear();
  const hours: string = date.getHours().toString().padStart(2, "0");
  const minutes: string = date.getMinutes().toString().padStart(2, "0");
  return (
    `${month} ${day}, ${year}` +
    (withTimeOfDay ? `, at ${hours}:${minutes}` : "")
  );
}

/**
 * @description Converts a __{@link xUpkeepConfig}__ object to a Date object.
 * @param upkeepConfig The upkeepConfig object to parse.
 * @returns A Date object, representing the next trigger Date.
 */
export function parseDate(
  upkeepConfig: xUpkeepConfig,
  nowSeconds: number,
): Date {
  const offset = nowSeconds - (nowSeconds % Number(upkeepConfig.period));
  for (const trigger of upkeepConfig.triggers) {
    const threshold: number = offset + Number(trigger);
    if (nowSeconds < threshold) {
      return new Date(threshold * 1000);
    }
  }

  // In case no trigger comes after the current time,
  // set the date to the next period's first trigger.
  return new Date(
    (offset + Number(upkeepConfig.period) + Number(upkeepConfig.triggers[0])) *
      1000,
  );
}

// export function sortCharities(charities: Charity[]): Charity[] {
//   if (charities.length === 0) {
//     return [];
//   }

//   // Sort enabled charities by checking previous and next
//   const sortedCharities: Charity[] = [];
//   let currentCharity: Charity = charities[0];
//   const first: Address | undefined = currentCharity.address;
//   do {
//     sortedCharities.push(currentCharity);
//     const temp: Charity | undefined = charities.find(
//       (charity) => charity.previous === currentCharity?.address,
//     );
//     if (temp === undefined) break;
//     else currentCharity = temp;
//   } while (currentCharity.address !== first);

//   // Return enabled and disabled charities
//   return charities;
// }

export const emptyTickets = (
  amount: number,
  numberPool?: bigint,
  starPool?: bigint,
): TicketMapper[] => {
  return Array.from({ length: amount }, (_, index) =>
    TicketMapper.empty(index, Number(numberPool ?? 0n), Number(starPool ?? 0n)),
  );
};

export const getNow = () => {
  return new Date().getTime();
};

/**
 * Handle Transaction Hash
 * @param hash The transaction hash to handle.
 * @param onSuccess The callback function to be called when the transaction is successful.
 * @param onError The callback function to be called when the transaction reverts.
 */
export async function handleTransactionHash(
  client: Client,
  hash: `0x${string}`,
  onSuccess: (hash: Address) => void,
  onRevert: (hash: Address) => void,
) {
  const result = await waitForTransactionReceipt(client!, {
    hash: hash,
  });
  if (result.status === "success") {
    onSuccess(hash);
  } else {
    onRevert(hash);
  }
}

/**
 * Handle Transaction Status
 */
export async function handleTransactionStatus(
  client: Client,
  id: string,
  onSuccess: (hash: Address) => void,
  onRevert: (hash: Address) => void,
) {
  const { status, receipts } = await waitForCallsStatus(client, { id });
  if (status === "success") {
    onSuccess(receipts![0].transactionHash);
  } else {
    onRevert(receipts![0].transactionHash);
  }
}

/**
 * Open Transaction URL.
 * @param hash The transaction hash to open.
 */
export async function openTransactionUrl(hash: Address) {
  const txUrl = `${CHAIN.blockExplorers.default.url}/tx/${hash}`;
  if (!isMiniApp) window.open(txUrl, "_blank", "noopener,noreferrer");
  else await sdk.actions.openUrl(txUrl);
}

export const generateColor = (percent: number): string => {
  return `hsl(${percent * 360}, 50%, 50%)`;
};

export const randomTicket = (
  numberPool: number,
  starPool: number,
  numbersPerTicket: number,
  starsPerTicket: number,
): Ticket => {
  const numbers: bigint[] = [];
  while (numbers.length < numbersPerTicket) {
    const number = Math.floor(Math.random() * numberPool) + 1;
    if (!numbers.includes(BigInt(number))) {
      numbers.push(BigInt(number));
    }
  }
  const stars: bigint[] = [];
  while (stars.length < starsPerTicket) {
    const star = Math.floor(Math.random() * starPool) + 1;
    if (!stars.includes(BigInt(star))) {
      stars.push(BigInt(star));
    }
  }
  return new Ticket(numbers.sort(), stars.sort());
};

/**
 * Find the prize (based on the draw's prize breakdown) for a given ticket.
 */

export const getTicketPrizes = (
  ticket: Ticket,
  winningTicket: Ticket,
  prizeTierResults?: PrizeTierResults[],
): USDC => {
  if (prizeTierResults == undefined || prizeTierResults.length == 0) {
    return USDC.empty();
  }
  const ticketNumbersSet = new Set(ticket.mainNumbers);
  const ticketStarsSet = new Set(ticket.luckyStars);
  const ticketMatch: xSet = {
    mainNumbers: 0n,
    luckyStars: 0n,
  };
  for (const num of winningTicket.mainNumbers) {
    if (ticketNumbersSet.has(BigInt(num))) {
      ticketMatch.mainNumbers!++;
    }
  }
  for (const num of winningTicket.luckyStars) {
    if (ticketStarsSet.has(BigInt(num))) {
      ticketMatch.luckyStars!++;
    }
  }
  return (
    prizeTierResults.find((prizeTier) =>
      combinationEquals(prizeTier.combination, ticketMatch),
    )?.prizePerWinner ?? USDC.empty()
  );
};

export const truncateMiddle = (
  str: string,
  frontLen: number,
  backLen: number,
): string => {
  if (str.length <= frontLen + backLen) {
    return str;
  }
  return (
    str.substring(0, frontLen) + "..." + str.substring(str.length - backLen)
  );
};

// Magic (don't question it)
export const mumboJumbo = (n: number, maxLength: number): string => {
  return new USDC(BigInt(Math.round(n))).pretty
    .substring(0, maxLength ?? 0)
    .padEnd(maxLength ?? 0, "0");
};

// Share with MiniApp
export async function shareMiniApp(text: string, url: string) {
  // Farcaster Miniapp SDK share
  try {
    const result = await sdk.actions.composeCast({
      text: text,
      embeds: [url],
    });
    if (result.cast) {
      console.log("User shared cast:", result.cast);
    } else {
      console.log("User cancelled.");
    }
  } catch {
    console.log("Failed to share via Farcaster Miniapp.");
  }
}
